import forIn from 'lodash/forIn';
import mousetrap from 'mousetrap';

import * as Constants from '../constants';

// Set the priority of the keySets here, from most important to least important
const KeySetPriority = [
  Constants.Keyboard.KeySetKeys.AUTOCOMPLETE,
  Constants.Keyboard.KeySetKeys.MODAL,
  Constants.Keyboard.KeySetKeys.REGISTER,
  Constants.Keyboard.KeySetKeys.REDEEM,
  Constants.Keyboard.KeySetKeys.SETTINGS,
  Constants.Keyboard.KeySetKeys.LOGIN,
  Constants.Keyboard.KeySetKeys.VOLUMESLIDER,
  Constants.Keyboard.KeySetKeys.NOWPLAYING,
  Constants.Keyboard.KeySetKeys.TUNER_SCRUBBER,
  Constants.Keyboard.KeySetKeys.TUNER,
].reverse();

// Add empty objects for each keyset to hold the handlers
const KeySets: Record<string, Record<string, $TSFixMe>> = {
  [Constants.Keyboard.KeySetKeys.MODAL]: {},
  [Constants.Keyboard.KeySetKeys.REGISTER]: {},
  [Constants.Keyboard.KeySetKeys.TUNER]: {},
  [Constants.Keyboard.KeySetKeys.LOGIN]: {},
  [Constants.Keyboard.KeySetKeys.REDEEM]: {},
  [Constants.Keyboard.KeySetKeys.SETTINGS]: {},
  [Constants.Keyboard.KeySetKeys.VOLUMESLIDER]: {},
  [Constants.Keyboard.KeySetKeys.NOWPLAYING]: {},
  [Constants.Keyboard.KeySetKeys.TUNER_SCRUBBER]: {},
  [Constants.Keyboard.KeySetKeys.AUTOCOMPLETE]: {},
};

interface IKeyBindingArgs {
  handler: unknown;
  isGlobal?: boolean;
  key: string;
  keySet: keyof typeof KeySets;
}
interface IKeyUnBindingArgs {
  isGlobal: boolean;
  key: string;
  keySet: keyof typeof KeySets;
}

// Allow Mousetrap to save global key handlers
// https://github.com/ccampbell/mousetrap/blob/master/plugins/global-bind/mousetrap-global-bind.js
(function (Mousetrap): void {
  const _globalCallbacks = {};
  const _originalStopCallback = Mousetrap.prototype.stopCallback;
  Mousetrap.prototype.stopCallback = function (
    e: $TSFixMe,
    element: $TSFixMe,
    combo: $TSFixMe,
    sequence: $TSFixMe,
  ): boolean {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    if (self.paused) {
      return true;
    }
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    if (_globalCallbacks[combo] || _globalCallbacks[sequence]) {
      return false;
    }
    return _originalStopCallback.call(self, e, element, combo);
  };
  Mousetrap.prototype.bindGlobal = function (keys: $TSFixMe, callback: $TSFixMe, action: $TSFixMe) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    self.bind(keys, callback, action);
    if (keys instanceof Array) {
      for (let i = 0; i < keys.length; i++) {
        // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        _globalCallbacks[keys[i]] = true;
      }
      return;
    }
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    _globalCallbacks[keys] = true;
  };
  // https://github.com/ccampbell/mousetrap/pull/353/files
  Mousetrap.prototype.unbindGlobal = function (keys: $TSFixMe, action: $TSFixMe): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    self.unbind(keys, action);
    if (keys instanceof Array) {
      for (let i = 0; i < keys.length; i++) {
        // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        _globalCallbacks[keys[i]] = false;
      }
      return;
    }
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    _globalCallbacks[keys] = false;
  };
  (Mousetrap as $TSFixMe).init();
})(mousetrap);

function rebindHandlers(): void {
  // Go through KeySets from lowest to highest and bind key events
  KeySetPriority.forEach(keySetName => {
    forIn(KeySets[keySetName], ({ handler, isGlobal }, key) => {
      if (isGlobal) {
        // @ts-expect-error TS(2339) FIXME: Property 'bindGlobal' does not exist on type 'Mous... Remove this comment to see the full error message
        mousetrap.bindGlobal(key, handler);
      } else {
        mousetrap.bind(key, handler);
      }
    });
  });
}

/**
 * Bind a keyboard key to a handler function
 *
 * @param {string} keySet The name of the keyset
 * @param {string} key The key
 * @param {Function} handler The handler
 */

export function bindKey({ keySet, key, handler, isGlobal }: IKeyBindingArgs): void {
  KeySets[keySet][key] = { handler, isGlobal };
  rebindHandlers();
}

/**
 * Unbind a keyboard handler
 *
 * @param {string} keySet The keyset that the key belongs to
 * @param {string} key The key to unbind
 */
export function unBindKey({ keySet, key, isGlobal }: IKeyUnBindingArgs): void {
  delete KeySets[keySet][key];
  if (isGlobal) {
    // @ts-expect-error TS(2339) FIXME: Property 'unbindGlobal' does not exist on type 'Mo... Remove this comment to see the full error message
    mousetrap.unbindGlobal(key);
  } else {
    mousetrap.unbind(key);
  }
  rebindHandlers();
}

/**
 * Bind a set of keyboard handlers
 *
 * @param {string} keySet The name of the keyset
 * @param {Array} keySpecs `bindKey` objects for each key to bind
 */
export function bindKeySet({
  keySet,
  keySpecs,
}: {
  keySet: keyof typeof KeySets;
  keySpecs: unknown[];
}): void {
  keySpecs.forEach((keySpec: $TSFixMe) => {
    const { key, handler } = keySpec;
    bindKey({
      keySet,
      key,
      handler,
    });
  });
}

/**
 * Unbind a keyset
 *
 * @param {string} keySetName The name of the keyset
 */
export function unBindKeySet(keySetName: keyof typeof KeySets): void {
  // go through each keybinding in the set and unbind it
  const keySet = KeySets[keySetName];
  forIn(keySet, ({ isGlobal }, key) => {
    unBindKey({ keySet: keySetName, key, isGlobal });
  });
  // make sure the next highest ones are re-bound

  rebindHandlers();
}
